import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Layout } from '../../components/Layout/Layout';
import { Table } from '../../components/UI/Table';
import { Pagination } from '../../components/UI/Pagination';
import { Filters } from '../../components/UI/Filters';
import { SessionEntity, SessionStatus, PaginatedResponse } from '../../types';
import { sessionsApi } from '../../services/api';
import { format } from 'date-fns';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 24px;
  color: var(--tg-theme-text-color, #000);
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const PageDescription = styled.p`
  margin: 0;
  color: var(--tg-theme-hint-color, #666);
  font-size: 14px;
`;

const StatusBadge = styled.span<{ status: SessionStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: var(--radius-lg);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => {
    switch (props.status) {
      case SessionStatus.Pending: return 'var(--warning-600)';
      case SessionStatus.InProgress: return 'var(--info-600)';
      case SessionStatus.Done: return 'var(--success-600)';
      case SessionStatus.Failed: return 'var(--error-600)';
      case SessionStatus.Canceled: return 'var(--gray-600)';
      default: return 'var(--gray-600)';
    }
  }};
  background: ${props => {
    switch (props.status) {
      case SessionStatus.Pending: return 'var(--warning-50)';
      case SessionStatus.InProgress: return 'var(--info-50)';
      case SessionStatus.Done: return 'var(--success-50)';
      case SessionStatus.Failed: return 'var(--error-50)';
      case SessionStatus.Canceled: return 'var(--gray-100)';
      default: return 'var(--gray-100)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.status) {
      case SessionStatus.Pending: return 'var(--warning-200)';
      case SessionStatus.InProgress: return 'var(--info-200)';
      case SessionStatus.Done: return 'var(--success-200)';
      case SessionStatus.Failed: return 'var(--error-200)';
      case SessionStatus.Canceled: return 'var(--gray-300)';
      default: return 'var(--gray-300)';
    }
  }};
  
  &::before {
    content: ${props => {
      switch (props.status) {
        case SessionStatus.Pending: return '"⏳"';
        case SessionStatus.InProgress: return '"🔄"';
        case SessionStatus.Done: return '"✅"';
        case SessionStatus.Failed: return '"❌"';
        case SessionStatus.Canceled: return '"⏹️"';
        default: return '"❓"';
      }
    }};
    font-size: 10px;
  }
`;

const getStatusLabel = (status: SessionStatus): string => {
  switch (status) {
    case SessionStatus.Pending: return 'Pending';
    case SessionStatus.InProgress: return 'In Progress';
    case SessionStatus.Done: return 'Done';
    case SessionStatus.Failed: return 'Failed';
    case SessionStatus.Canceled: return 'Canceled';
    default: return 'Unknown';
  }
};

export const Sessions: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SessionEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState<number | string>('');
  const [sortBy, setSortBy] = useState<string>('scheduleTime');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  const pageSize = 10;

  const statusOptions = [
    { value: SessionStatus.Pending, label: 'Pending' },
    { value: SessionStatus.InProgress, label: 'In Progress' },
    { value: SessionStatus.Done, label: 'Done' },
    { value: SessionStatus.Failed, label: 'Failed' },
    { value: SessionStatus.Canceled, label: 'Canceled' }
  ];

  const columns = [
    {
      key: 'id',
      title: 'ID',
      sortable: true
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      render: (status: SessionStatus) => (
        <StatusBadge status={status}>
          {getStatusLabel(status)}
        </StatusBadge>
      )
    },
    {
      key: 'scheduleTime',
      title: 'Schedule Time',
      sortable: true,
      render: (scheduleTime: string) => format(new Date(scheduleTime), 'MMM dd, yyyy HH:mm')
    },
    {
      key: 'operatorPerCall',
      title: 'Operators',
      sortable: true
    },
    {
      key: 'callsCount',
      title: 'Calls',
      render: (_: any, record: SessionEntity) => record.calls?.length || 0
    },
    {
      key: 'scheduleTime',
      title: 'Created',
      sortable: true,
      render: (scheduleTime: string) => scheduleTime ? format(new Date(scheduleTime), 'MMM dd, yyyy') : 'N/A'
    }
  ];

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: PaginatedResponse<SessionEntity> = await sessionsApi.getPaged({
        page: currentPage,
        pageSize,
        sessionStatus: statusFilter === '' ? undefined : Number(statusFilter),
        sortLabel: sortBy || undefined,
        sortDirection: sortDirection === 'asc' ? 'Asc' : 'Desc'
      });

      setSessions(response.data);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalCount);
    } catch (error: any) {
      console.error('Error fetching sessions:', error);
      if (error.response?.status === 401) {
        setError('Authentication failed. Please check your API credentials.');
      } else if (error.response?.status === 403) {
        setError('Access forbidden. You may not have permission to view sessions.');
      } else if (error.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Failed to load sessions. Please check your connection and try again.');
      }
      setSessions([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [currentPage, statusFilter, sortBy, sortDirection]);

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDirection('asc');
    }
  };

  const handleRowClick = (session: SessionEntity) => {
    navigate(`/session-calls/${session.id}`);
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handleStatusChange = (status: number | string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  return (
    <Layout title="Sessions">
      <Container>
        <PageHeader>
          <PageTitle>Sessions</PageTitle>
          <PageDescription>Manage and view session data</PageDescription>
        </PageHeader>
        
        <Filters
          statusFilter={statusFilter}
          statusOptions={statusOptions}
          onStatusChange={handleStatusChange}
          onClear={handleClearFilters}
        />

        {error && (
          <div style={{
            padding: '16px',
            margin: '16px 0',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '8px',
            color: '#a00'
          }}>
            <strong>Error:</strong> {error}
            <button
              onClick={() => fetchSessions()}
              style={{
                marginLeft: '16px',
                padding: '4px 8px',
                backgroundColor: '#a00',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        )}

        <Table
          columns={columns}
          data={sessions}
          loading={loading}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={handleSort}
          onRowClick={handleRowClick}
        />
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={setCurrentPage}
        />
      </Container>
    </Layout>
  );
};